import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { get } from 'network/api';

import { encodeId } from 'lib/url';

import LoadingPage from './Loading';
import FormUser from './ComponentsPage/FormUser';
import SectionSummaryOrder from './ComponentsPage/SectionSummaryOrder';
import Method from './ComponentsPage/Method';
import Image from 'components/Image';
import SuccessResult from './ComponentsPage/SuccessResult';

type ApiError = {
  detail: string;
  status_code: number;
};

const CheckoutPages: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { id, name } = query as { id?: string; name?: string };
  const [pickPayment, setPickPayment] = useState(false);

  const getDetailCheckout = async (id: string) => {
    const encodedId = encodeId(id);
    return get(`/checkout/v1/detail?invoice_number=${encodedId}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, isLoading, error } = useQuery<any, ApiError>({
    queryKey: ['getDetailCheckout', id],
    queryFn: async () => {
      if (!id) throw new Error('ID is required');
      return getDetailCheckout(id);
    },
    enabled: !!id,
    retry: false,
  });

  useEffect(() => {
    if (error || !id) {
      navigate('/error', {
        state: {
          message: error
            ? error.detail
            : 'Terjadi kesalahan yang tidak diketahui',
        },
      });
    }
  }, [error, navigate, id]);

  const checkPayment = () => {
    window.location.reload();
  };

  const businessSettings = {
    primary_color: data?.data?.business?.primary_color
      ? data?.data?.business?.primary_color
      : '#F5F5F5',
    secondary_color: data?.data?.business?.secondary_color
      ? data?.data?.business?.secondary_color
      : '#FAFAFA',
    sidebar_text_color: data?.data?.business?.sidebar_text_color
      ? data?.data?.business?.sidebar_text_color
      : '#111827',
    sidebar_text_secondary_color: data?.data?.business
      ?.sidebar_text_secondary_color
      ? data?.data?.business?.sidebar_text_secondary_color
      : '#6b7280',
    button_color: data?.data?.business?.button_color
      ? data?.data?.business?.button_color
      : '#5D87FF',
    button_text_color: data?.data?.business?.button_text_color
      ? data?.data?.business?.button_text_color
      : '#FFFFFF',
    default: data?.data?.business?.default
      ? data?.data?.business?.default
      : '#D9D9D9',
    theme_name: data?.data?.business?.theme_name
      ? data?.data?.business?.theme_name
      : 'Default',
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  const getPaymentStatus = (): boolean => {
    const mainStatus = data?.data?.status === 'ACTIVE';

    if (data?.data?.payment?.string) {
      const isCardPayment = data?.data?.payment?.string === 'cards';

      if (isCardPayment) {
        return (
          data?.data?.payment?.status === 'ACTIVE' ||
          data?.data?.payment?.status === true
        );
      }

      return mainStatus;
    }

    return mainStatus;
  };

  const isPaymentActive = getPaymentStatus();

  return (
    <div className="h-screen relative">
      <main className="h-full grid grid-cols-6">
        <h1 className="sr-only">Checkout</h1>
        <section
          aria-labelledby="payment-heading"
          className={`${
            isPaymentActive
              ? 'col-span-6 lg:col-span-4'
              : 'col-span-12 lg:col-span-12'
          } overflow-y-auto relative`}
        >
          <div className="mx-auto max-w-xl h-full flex justify-center">
            <div className="w-full h-fit">
              {isPaymentActive ? (
                <div>
                  {name ? (
                    <Method
                      data={data?.data}
                      pickPayment={pickPayment}
                      setPickPayment={setPickPayment}
                      settings={businessSettings}
                    />
                  ) : (
                    <FormUser data={data?.data} settings={businessSettings} />
                  )}
                </div>
              ) : (
                <SuccessResult data={data?.data} />
              )}
              <div className="hidden lg:block mx-auto max-w-fit mt-1 mb-20">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-[10px]">Powered by</span>
                  <Image
                    className="object-contain w-[80px] h-[40px]"
                    src={'/images/logos/indorayalogo.png'}
                    alt="Business Logo"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {isPaymentActive && (
          <SectionSummaryOrder data={data?.data} settings={businessSettings} />
        )}
      </main>
      {pickPayment && isPaymentActive && (
        <div className="block lg:hidden sticky bottom-0 left-0 right-0 w-full bg-white py-2 px-4 border-t border-gray-300">
          <button
            onClick={() => checkPayment()}
            className={`w-full flex flex-row items-center gap-2 justify-center rounded-md border border-transparent px-4 py-2.5 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2`}
            style={{
              backgroundColor: businessSettings.button_color,
              color: businessSettings.button_text_color,
            }}
          >
            {'CHECK STATUS'}
          </button>
          <div className="mx-auto max-w-fit mt-1">
            <div className="flex flex-row items-center gap-2">
              <span className="text-[10px]">Powered by</span>
              <Image
                className="object-contain w-[80px] h-[40px]"
                src={'/images/logos/indorayalogo.png'}
                alt="Business Logo"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPages;
